import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calendar, Clock, CheckCircle, AlertTriangle, X, Wallet, Zap,
  Info, ExternalLink, Trash2, RefreshCw, ChevronRight, PauseCircle, Copy,
  ChevronDown, ChevronUp, TrendingUp,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import TradingViewChart from '../components/TradingViewChart';
import { Spinner, InfoTip } from '../components/ui';
import {
  quoteDexDcaPlan, prepareDexDcaSlice, commitDexDcaPlan,
  listDexDcaPlans, cancelDexDcaPlan,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor, chartPairFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const FREQUENCY_OPTIONS = [
  { id: 'daily',    label: 'Daily',     sec: 86400 },
  { id: 'weekly',   label: 'Weekly',    sec: 7 * 86400 },
  { id: 'biweekly', label: 'Bi-weekly', sec: 14 * 86400 },
  { id: 'monthly',  label: 'Monthly',   sec: 30 * 86400 },
];

const SLICE_COUNT_OPTIONS = [4, 8, 12, 24];

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;
// CoW VaultRelayer is constant across chains. Velora's Delta contract is
// chain-specific; we pull it from the first prepare-slice response.
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function fmtFutureTime(sec) {
  try { return new Date(sec * 1000).toLocaleString(); } catch { return String(sec); }
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

// ─── How DCA works ───────────────────────────────────────────────────────────
function HowItWorksPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-blue-500/10 transition-colors">
        <div className="flex items-center gap-3">
          <Info className="w-4 h-4 text-blue-400" />
          <span className="text-blue-300 text-sm font-medium">How DCA works — read this before your first plan</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-blue-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-3 text-xs text-gray-300 leading-relaxed">
          <div>
            <p className="text-blue-300 font-semibold mb-1">1. You configure a plan.</p>
            <p>Pick what to spend (e.g. USDC), what to buy (e.g. WETH), how much to spend per execution ("per slice"), how often (daily/weekly), and how many total executions ("slices"). 4 weekly slices of 25 USDC = 100 USDC total spread over 4 weeks.</p>
          </div>
          <div>
            <p className="text-blue-300 font-semibold mb-1">2. You sign once per slice.</p>
            <p>One MetaMask popup per slice. 4 slices = 4 signatures back-to-back. Signing is free (no gas). Each signed order tells a resolver "you can fill this on or after the scheduled time, at this price."</p>
          </div>
          <div>
            <p className="text-blue-300 font-semibold mb-1">3. You approve the spend token once.</p>
            <p>Before signing, your wallet asks for ERC-20 approval (one-time, ~$0.05 gas on Base/L2s). This lets the protocol pull your tokens when an order fills. You never send tokens upfront — only at fill time.</p>
          </div>
          <div>
            <p className="text-blue-300 font-semibold mb-1">4. Slices fill autonomously on schedule.</p>
            <p>Each slice fires at its scheduled time. Resolvers (CoW solvers or 1inch professional fillers) compete to fill at the best price. You get a notification on every fill. No further action needed from you.</p>
          </div>
          <div className="pt-2 border-t border-blue-500/20">
            <p className="text-blue-300 font-semibold mb-1">Market DCA vs Limit DCA</p>
            <p><span className="text-white">Market DCA</span> — buys at current market price each slice with your slippage cushion. Typical DCA behavior: always fills as long as market hasn't moved more than your slippage. Default: 1% slippage.</p>
            <p className="mt-1"><span className="text-white">Limit DCA</span> — only fills if the market is at or better than your fixed price. Disciplined buying — slices skip if the market is unfavorable. Good when you have a strong opinion on price.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Preset DCA templates — one-click form fills tuned for common use cases.
// Conservative = patient & low risk; Standard = balanced; Aggressive = fast.
// Token pair stays whatever the user has selected — only schedule + pricing
// shape changes.
const DCA_TEMPLATES = [
  { id: 'conservative', label: 'Conservative', desc: 'Weekly × 8 · 2% slippage', freqId: 'weekly',   totalSlices: 8,  slippagePct: 2 },
  { id: 'standard',     label: 'Standard',     desc: 'Weekly × 4 · 1% slippage', freqId: 'weekly',   totalSlices: 4,  slippagePct: 1 },
  { id: 'aggressive',   label: 'Aggressive',   desc: 'Daily × 14 · 1% slippage', freqId: 'daily',    totalSlices: 14, slippagePct: 1 },
  { id: 'monthly',      label: 'Long-term',    desc: 'Monthly × 12 · 2% slippage', freqId: 'monthly', totalSlices: 12, slippagePct: 2 },
];

// Phase 5 — execution profile options. TWAP is the legacy default. VWAP
// weights slice sizes by typical hour-of-UTC volume so heavier sizes hit
// peak liquidity. Iceberg uses 4× more slices at 1/4 the size each to
// camouflage total notional from anyone watching the orderbook.
const EXECUTION_PROFILE_OPTIONS = [
  { id: 'twap',    label: 'TWAP',    title: 'Equal slices',
    desc: 'Same size every slice. Simple and predictable. Default DCA behavior.' },
  { id: 'vwap',    label: 'VWAP',    title: 'Volume-weighted',
    desc: 'Slice sizes follow typical liquidity peaks — heavier during US trading hours, lighter overnight. Lower expected slippage for the same total notional.' },
  { id: 'iceberg', label: 'Iceberg', title: 'Hidden size',
    desc: '4× more slices at 1/4 the size each, fired at 1/4 the interval. Camouflages your total notional so observers can\'t front-run your accumulation.' },
];

// Minimal ERC20 ABI fragment — balanceOf only — for wallet balance preview
const ERC20_BALANCE_ABI = [{
  name: 'balanceOf', type: 'function', stateMutability: 'view',
  inputs: [{ name: 'owner', type: 'address' }],
  outputs: [{ type: 'uint256' }],
}];

// ─── Create-plan wizard ──────────────────────────────────────────────────────
function CreatePlanPanel({ onCreated, onToast, onPairChange, initialValues, formKey }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [protocol, setProtocol]       = useState(initialValues?.protocol  || 'cow');
  const [chainId,  setChainId]        = useState(initialValues?.chainId   || connectedChainId || 1);
  const [sellSym,  setSellSym]        = useState(initialValues?.sellSym   || 'USDC');
  const [buySym,   setBuySym]         = useState(initialValues?.buySym    || 'WETH');
  const [sellPerSlice, setSellPerSlice] = useState(initialValues?.sellPerSlice || '');
  const [totalSlices, setTotalSlices] = useState(initialValues?.totalSlices || 8);
  const [freqId,    setFreqId]        = useState(initialValues?.freqId    || 'weekly');
  const [pricingModel, setPricingModel] = useState(initialValues?.pricingModel || 'market');
  const [slippagePct, setSlippagePct] = useState(initialValues?.slippagePct ?? 1);
  const [limitPrice, setLimitPrice]   = useState(initialValues?.limitPrice || '');
  const [planName,   setPlanName]     = useState(initialValues?.name || '');
  const [customTick, setCustomTick]   = useState(0);   // bump to re-pull tokens after a custom add
  // DCA is recurring-only (weekly/monthly/etc.). For one-shot TWAP/VWAP/Iceberg
  // execution intents, users go to the dedicated Algo Orders page instead.
  const executionProfile = 'twap';

  // Wizard state — null = idle, otherwise we're mid-create
  const [wizard, setWizard] = useState(null);
  // wizard shape: { phase: 'quoting' | 'approving' | 'signing' | 'committing',
  //                  planId, schedule, perSliceTakingAmount, signedSoFar, signedSlices, error }

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));
  const frequency = FREQUENCY_OPTIONS.find(f => f.id === freqId);

  // Wallet balance preview — read ERC20.balanceOf via the existing publicClient
  // inside an effect (controlled call) so we don't hit wagmi's hook validation
  // when the token/address is briefly undefined during initial render.
  const [rawBalance, setRawBalance] = useState(null);
  useEffect(() => {
    if (!publicClient || !sellToken?.address || !connectedAddr || Number(connectedChainId) !== Number(chainId)) {
      setRawBalance(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const bal = await publicClient.readContract({
          address: sellToken.address,
          abi: ERC20_BALANCE_ABI,
          functionName: 'balanceOf',
          args: [connectedAddr],
        });
        if (!cancelled) setRawBalance(bal);
      } catch {
        if (!cancelled) setRawBalance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [publicClient, sellToken?.address, connectedAddr, connectedChainId, chainId]);
  const balanceHuman = useMemo(() => {
    if (rawBalance == null || !sellToken) return null;
    return formatBaseUnits(rawBalance.toString(), sellToken.decimals, 4);
  }, [rawBalance, sellToken]);
  const balanceShort = balanceHuman == null ? null
    : Number(balanceHuman).toLocaleString(undefined, { maximumFractionDigits: 4 });

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  // Bubble token selection up so the page-level chart can follow it
  useEffect(() => {
    onPairChange?.({ sellSym, buySym, chainId });
  }, [sellSym, buySym, chainId, onPairChange]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) {
      setChainId(PROTOCOL_CHAINS[protocol][0]);
    }
  }, [protocol, chainId]);

  // Market DCA isn't supported on Polygon (CoW doesn't quote there). Force to
  // limit pricing when user selects Polygon, with a clear note.
  useEffect(() => {
    if (chainId === 137 && pricingModel === 'market') setPricingModel('limit');
  }, [chainId, pricingModel]);

  const totalSpend = useMemo(() => {
    const a = Number(sellPerSlice), n = Number(totalSlices);
    if (!Number.isFinite(a) || !Number.isFinite(n) || a <= 0) return null;
    return a * n;
  }, [sellPerSlice, totalSlices]);

  const canStart =
    isConnected && connectedAddr &&
    Number(connectedChainId) === Number(chainId) &&
    sellToken && buyToken && sellToken.symbol !== buyToken.symbol &&
    Number(sellPerSlice) > 0 &&
    (pricingModel === 'market' || (pricingModel === 'limit' && Number(limitPrice) > 0)) &&
    !wizard;

  async function ensureAllowance(spender, requiredTotal) {
    async function readAllowance() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await readAllowance();
    if (allowance >= requiredTotal) return;

    if (allowance > 0n) {
      // USDT zero-reset edge case
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await readAllowance(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm in 3 min');
    }

    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await readAllowance(); } catch {}
      if (allowance >= requiredTotal) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleStart() {
    if (!canStart) return;
    try {
      const sellAmountPerSlice = toBaseUnits(sellPerSlice, sellToken.decimals);
      // Note: when executionProfile === 'iceberg', the backend expands slice
      // count by 4×. totalSell remains user's intended total (perSlice × baseSlices).
      const totalSell = BigInt(sellAmountPerSlice) * BigInt(totalSlices);

      // Phase 1: quote plan — backend returns a schedule annotated with
      // per-slice sellAmount + takingAmount + weight (for all profiles).
      setWizard({ phase: 'quoting', planId: null, schedule: null, perSliceTakingAmount: null, signedSlices: [], error: null });
      const quote = await quoteDexDcaPlan({
        protocol, chainId,
        owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmountPerSlice,
        pricingModel,
        slippagePct: pricingModel === 'market' ? Number(slippagePct) : undefined,
        limitPrice:  pricingModel === 'limit'  ? String(limitPrice)   : undefined,
        intervalSec: frequency.sec,
        totalSlices,
        executionProfile, // Phase 5
      });

      const { planId, schedule, perSliceTakingAmount, effectiveSlices } = quote.data;
      const sliceCount = effectiveSlices || schedule.length;

      // Phase 2: prepare each slice + sign. For Velora the spender comes back
      // in the first prepare response — we approve once we know it. For CoW
      // the spender is constant so we approve first to save a roundtrip.
      const signedSlices = [];
      let resolvedSpender = protocol === 'cow' ? COW_SPENDER : null;
      let approved = false;
      async function approveOnceWeKnowSpender() {
        if (approved || !resolvedSpender) return;
        setWizard(w => ({ ...w, phase: 'approving', planId, schedule, perSliceTakingAmount }));
        await ensureAllowance(resolvedSpender, totalSell);
        approved = true;
      }
      // For CoW, approve upfront
      await approveOnceWeKnowSpender();

      for (let i = 0; i < sliceCount; i++) {
        const slot = schedule[i];
        // Phase 5: each slot carries its own sellAmount + takingAmount (uniform
        // for TWAP, varying for VWAP/Iceberg). Fall back to the reference
        // values if the backend response lacks per-slice fields (older payload).
        const sliceSellAmount   = slot.sellAmount   || sellAmountPerSlice;
        const sliceTakingAmount = slot.takingAmount || perSliceTakingAmount;
        const prep = await prepareDexDcaSlice({
          protocol, chainId,
          maker: connectedAddr,
          makerAsset: sellToken.address,
          takerAsset: buyToken.address,
          makingAmount: sliceSellAmount,
          takingAmount: sliceTakingAmount,
          sliceIndex: slot.sliceIndex,
          scheduledAt: slot.scheduledAt,
          // Velora needs decimals for the per-slice quote
          srcDecimals:  sellToken.decimals,
          destDecimals: buyToken.decimals,
        });
        // Capture spender for Velora on first slice + approve before signing
        if (!resolvedSpender && prep.data.spender) resolvedSpender = prep.data.spender;
        await approveOnceWeKnowSpender();

        setWizard(w => ({ ...w, phase: 'signing', signedSoFar: i, sliceCount }));
        const { typedData, order } = prep.data;
        const signature = await signTypedDataAsync({
          domain: typedData.domain, types: typedData.types,
          primaryType: typedData.primaryType, message: order,
        });
        signedSlices.push({
          sliceIndex:  slot.sliceIndex,
          scheduledAt: slot.scheduledAt,
          sellAmount:  sliceSellAmount,   // Phase 5 — persisted on schedule
          weight:      slot.weight,        // Phase 5
          order, signature,
          // Velora orderId comes back only after worker submits — leave undefined here.
          orderId: prep.data.orderHash || prep.data.orderUid || null,
        });
      }

      // Phase 4: commit
      setWizard(w => ({ ...w, phase: 'committing', signedSlices }));
      const commit = await commitDexDcaPlan({
        planId, protocol, chainId, owner: connectedAddr,
        name: planName?.trim() || undefined,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmountPerSlice, takingAmountPerSlice: perSliceTakingAmount,
        pricingModel, slippagePct: pricingModel === 'market' ? Number(slippagePct) : undefined,
        limitPrice: pricingModel === 'limit' ? String(limitPrice) : undefined,
        intervalSec: frequency.sec, totalSlices: sliceCount,
        executionProfile, // Phase 5
        slices: signedSlices,
      });

      const profileLabel = executionProfile === 'iceberg' ? `Iceberg (${sliceCount} mini-slices)`
                         : executionProfile === 'vwap'    ? `VWAP (${sliceCount} volume-weighted slices)`
                         : `${sliceCount} slices`;
      onToast({ type: 'success', message: `DCA plan created — ${profileLabel} scheduled. First one fires now, then every ${frequency.label.toLowerCase()}.` });
      setSellPerSlice('');
      setLimitPrice('');
      onCreated(commit.data?.plan);
      setWizard(null);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create DCA plan';
      onToast({ type: 'error', message: msg });
      setWizard(w => w ? { ...w, error: msg } : null);
      setTimeout(() => setWizard(null), 3000);
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center">
          <Calendar className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New DCA plan</h3>
          <p className="text-gray-500 text-xs">Sign once for each slice. Resolvers fill on schedule, no further action.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
              <p className="text-amber-300/80 text-xs mt-0.5">We'll never ask for your private key — only EIP-712 signatures per slice.</p>
            </div>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">
              Your wallet is on chain <span className="font-semibold">{CHAINS.find(c => c.id === Number(connectedChainId))?.label || `#${connectedChainId}`}</span>.
              This plan needs to be signed on <span className="font-semibold">{CHAINS.find(c => c.id === chainId)?.label}</span>.
            </p>
          </div>
          <button onClick={() => switchChain?.({ chainId })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
            {switchingChain ? <Spinner size="sm" /> : <Info className="w-4 h-4" />}
            {switchingChain ? 'Switching…' : `Switch wallet to ${CHAINS.find(c => c.id === chainId)?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Templates — one-click presets */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Quick start
            <InfoTip side="right">Templates pre-fill frequency + slice count + slippage. Conservative = Weekly × 8, Standard = Weekly × 4, Aggressive = Daily × 14, Long-term = Monthly × 12.</InfoTip>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {DCA_TEMPLATES.map(t => (
              <button key={t.id} type="button" onClick={() => {
                setFreqId(t.freqId); setTotalSlices(t.totalSlices); setSlippagePct(t.slippagePct); setPricingModel('market');
              }}
                className="px-3 py-2 rounded-xl text-left bg-dark-700 border border-dark-600 hover:bg-dark-600 transition-colors">
                <p className="text-white text-xs font-semibold">{t.label}</p>
                <p className="text-gray-500 text-[10px] mt-0.5 leading-tight">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Plan name (optional) */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Name <span className="text-gray-600 normal-case">(optional)</span></label>
          <input type="text" value={planName} onChange={e => setPlanName(e.target.value.slice(0, 60))}
            placeholder="e.g. ETH retirement fund"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>

        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW (Eth/Arb/Base)' : 'Velora (+Poly/BSC/OP/Avax)'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Sell + buy + amount */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Spend</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide flex items-center justify-between mb-1.5" title="The fixed amount of SPEND token you buy with each scheduled execution. E.g. 25 USDC per slice × 4 slices = 100 USDC total.">
              <span>Per slice <span className="text-gray-600">(?)</span></span>
              {balanceShort != null && (
                <button type="button" onClick={() => setSellPerSlice(balanceHuman)}
                  className="text-emerald-400/80 hover:text-emerald-300 normal-case text-[10px] font-mono"
                  title={`Use full balance · ${balanceHuman} ${sellSym}`}>
                  Bal: {balanceShort}
                </button>
              )}
            </label>
            <input type="number" min="0" step="any" value={sellPerSlice}
              onChange={e => setSellPerSlice(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Receive</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
            </select>
          </div>
        </div>

        {/* Custom-token resolver — paste any ERC-20 */}
        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Frequency
              <InfoTip side="top">How often each slice fires. Daily = 14-day catalyst plays. Weekly = standard accumulation. Monthly = long-term hold building. Bi-weekly = compromise.</InfoTip>
            </label>
            <select value={freqId} onChange={e => setFreqId(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {FREQUENCY_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Slices
              <InfoTip side="top">Total number of fills. You sign this many MetaMask popups back-to-back at plan start. Each fills on its scheduled date.</InfoTip>
            </label>
            <select value={totalSlices} onChange={e => setTotalSlices(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {SLICE_COUNT_OPTIONS.map(n => <option key={n} value={n}>{n} buys</option>)}
            </select>
          </div>
        </div>

        {/* Pricing model */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5"
            title="Market DCA buys at current market price each slice with a slippage cushion. Limit DCA only fills if the market is at or better than your fixed price.">
            Pricing <span className="text-gray-600">(?)</span>
          </label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button onClick={() => setPricingModel('market')} disabled={chainId === 137}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                pricingModel === 'market' ? 'bg-accent-primary/10 border-accent-primary/40 text-white'
                : chainId === 137 ? 'bg-dark-700 border-dark-600 text-gray-600 cursor-not-allowed'
                : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              Market DCA (quote ± slippage)
            </button>
            <button onClick={() => setPricingModel('limit')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                pricingModel === 'limit' ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              Limit DCA (fixed price)
            </button>
          </div>
          {pricingModel === 'market' ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">Slippage:</span>
              <input type="number" min="0.1" max="10" step="0.1"
                value={slippagePct} onChange={e => setSlippagePct(e.target.value)}
                className="w-20 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1 text-white text-xs text-center focus:outline-none focus:border-accent-primary" />
              <span className="text-gray-500 text-xs">%</span>
              <span className="text-gray-500 text-[11px] ml-2">Wider = more fills, less price control</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">{buySym} per {sellSym}:</span>
              <input type="number" min="0" step="any" value={limitPrice}
                onChange={e => setLimitPrice(e.target.value)} placeholder="0.0"
                className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-accent-primary" />
            </div>
          )}
          {chainId === 137 && pricingModel === 'limit' && (
            <p className="text-gray-500 text-[10px] mt-1">Market DCA unavailable on Polygon (CoW quote required) — using Limit DCA only.</p>
          )}
        </div>

        {/* Plan summary */}
        {totalSpend != null && (
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Total spend</span><span className="text-white font-semibold">{totalSpend} {sellSym}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Slices</span><span className="text-white">{totalSlices} × {sellPerSlice || '0'} {sellSym}, {frequency.label.toLowerCase()}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Wallet signatures</span><span className="text-white">{totalSlices} (one per slice)</span></div>
          </div>
        )}

        {wizard ? (
          <WizardProgress wizard={wizard} totalSlices={wizard.sliceCount || totalSlices} />
        ) : (
          <button onClick={handleStart} disabled={!canStart}
            className="w-full btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40">
            <Zap className="w-4 h-4" /> Create DCA plan
          </button>
        )}

        <p className="text-gray-500 text-[11px] text-center">
          You'll approve {sellSym} once + sign one EIP-712 message per slice. After that, the platform schedules everything.
        </p>
      </div>
    </Card>
  );
}

function WizardProgress({ wizard, totalSlices }) {
  const { phase, signedSoFar = 0 } = wizard || {};
  let label = '';
  let icon = <Spinner size="sm" />;
  if (phase === 'quoting')    label = 'Quoting market price…';
  else if (phase === 'approving') label = 'Waiting for token approval (confirm in wallet)…';
  else if (phase === 'signing')   label = `Signing slice ${signedSoFar + 1} of ${totalSlices}…`;
  else if (phase === 'committing') label = 'Saving plan…';
  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
      {icon}
      <div className="flex-1 min-w-0">
        <p className="text-amber-300 text-sm font-semibold">{label}</p>
        {phase === 'signing' && (
          <div className="mt-1.5 h-1 rounded-full bg-dark-700 overflow-hidden">
            <div className="h-full bg-amber-400" style={{ width: `${(signedSoFar / totalSlices) * 100}%` }} />
          </div>
        )}
        {wizard?.error && <p className="text-red-400 text-xs mt-1">{wizard.error}</p>}
      </div>
    </div>
  );
}

// CoW API base URLs — same as backend cowProtocolAdapter. Used by
// MarketPositionBadge to fetch the live spot rate without a backend hop.
const COW_API_BASES = {
  1:     'https://api.cow.fi/mainnet',
  42161: 'https://api.cow.fi/arbitrum_one',
  8453:  'https://api.cow.fi/base',
  100:   'https://api.cow.fi/xdai',
};

// Shows the plan's locked-in fill rate vs the current spot market.
// Clarification: this is what each slice was SIGNED at (the limit price the
// user committed to). The actual fill may be slightly better via CoW surplus.
// Compared to the live spot quote, this tells the user whether their plan is
// presently above or below market — green = signed cheaper than current spot.
function MarketPositionBadge({ plan }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(false);

  useEffect(() => {
    let aborted = false;
    async function go() {
      // Only CoW-supported chains have a free public quote endpoint we can hit.
      const base = COW_API_BASES[Number(plan.chainId)];
      if (!base || plan.protocol !== 'cow') return; // skip silently for Velora/Polygon for now
      try {
        const res = await fetch(`${base}/api/v1/quote`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sellToken: plan.sellToken, buyToken: plan.buyToken,
            from: plan.owner,
            sellAmountBeforeFee: plan.sellAmountPerSlice,
            kind: 'sell',
          }),
        });
        if (!res.ok) throw new Error('quote');
        const json = await res.json();
        const liveBuy   = BigInt(json.quote?.buyAmount || 0);
        const signedBuy = BigInt(plan.takingAmountPerSlice || 0);
        if (signedBuy === 0n || liveBuy === 0n) return;
        // Difference: positive = your plan locked cheaper than now (good)
        const diffBp = Number(((liveBuy - signedBuy) * 10000n) / signedBuy);
        if (!aborted) setData({ liveBuy, signedBuy, diffBp });
      } catch {
        if (!aborted) setErr(true);
      }
    }
    go();
    return () => { aborted = true; };
  }, [plan.chainId, plan.sellToken, plan.buyToken, plan.sellAmountPerSlice, plan.takingAmountPerSlice, plan.owner, plan.protocol]);

  if (err || !data) return null;
  const diffPct = data.diffBp / 100;
  const better = diffPct > 0;
  const cls = better ? 'bg-emerald-500/10 text-emerald-400' : diffPct < 0 ? 'bg-red-500/10 text-red-400' : 'bg-dark-600 text-gray-400';
  const sign = better ? '+' : '';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}
      title={`Your plan signed at ${plan.takingAmountPerSlice} units of ${plan.buySymbol}; current market quote is ${data.liveBuy.toString()} units. Positive % = you locked a better rate.`}>
      vs market {sign}{diffPct.toFixed(2)}%
    </span>
  );
}

// ─── Plan row + plan list ────────────────────────────────────────────────────
function PlanRow({ plan, onCancel, onDuplicate, busy }) {
  const [expanded, setExpanded] = useState(false);
  const submitted = plan.schedule.filter(s => s.status === 'submitted' || s.status === 'filled').length;
  const filled    = plan.schedule.filter(s => s.status === 'filled').length;
  const failed    = plan.schedule.filter(s => s.status === 'failed').length;
  const remaining = plan.schedule.filter(s => s.status === 'pending').length;
  const nextSlice = plan.schedule.find(s => s.status === 'pending');

  // Realised accumulation (best-effort — sums takingAmountPerSlice for filled slices)
  const buyDec = plan.buyDecimals || 18;
  const sellDec = plan.sellDecimals || 18;
  const filledBuyBase = (() => {
    try {
      let total = 0n;
      for (const s of plan.schedule) {
        if (s.status === 'filled') total += BigInt(plan.takingAmountPerSlice || '0');
      }
      return total;
    } catch { return 0n; }
  })();
  const investedSellBase = (() => {
    try {
      let total = 0n;
      for (const s of plan.schedule) {
        if (s.status === 'filled' || s.status === 'submitted') total += BigInt(plan.sellAmountPerSlice || '0');
      }
      return total;
    } catch { return 0n; }
  })();

  const statusCls = {
    active:    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    completed: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    cancelled: 'bg-dark-600 text-gray-400 border-dark-500',
  }[plan.status] || 'bg-dark-600 text-gray-400 border-dark-500';

  function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

  function fmtSliceTime(unix) {
    try { return new Date(Number(unix) * 1000).toLocaleString(); } catch { return '—'; }
  }
  function shortDateMonth(unix) {
    try { return new Date(Number(unix) * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch { return '—'; }
  }

  const sliceStateCls = {
    filled:    'text-emerald-400',
    submitted: 'text-blue-300',
    failed:    'text-red-400',
    cancelled: 'text-gray-500',
    pending:   'text-gray-400',
  };

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            {plan.name && (
              <p className="text-white text-sm font-semibold mb-0.5 truncate">{plan.name}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-white ${plan.name ? 'text-xs' : 'text-sm'} font-semibold`}>
                {formatBaseUnits(plan.sellAmountPerSlice, plan.sellDecimals)} {plan.sellSymbol} → {plan.buySymbol}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${statusCls}`}>
                {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">
                {plan.protocol === 'cow' ? 'CoW' : plan.protocol === 'velora' ? 'Velora' : '1inch'}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">
                {plan.pricingModel === 'market' ? `Market ±${plan.slippagePct}%` : `Limit @ ${plan.limitPrice}`}
              </span>
              {plan.executionProfile && plan.executionProfile !== 'twap' && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border ${
                  plan.executionProfile === 'vwap'
                    ? 'bg-blue-500/10 text-blue-300 border-blue-500/30'
                    : 'bg-violet-500/10 text-violet-300 border-violet-500/30'
                }`} title={plan.executionProfile === 'vwap'
                  ? 'Volume-weighted slices — sizes follow typical liquidity peaks'
                  : 'Iceberg execution — 4× more slices at 1/4 the size each, camouflages total notional'}>
                  {plan.executionProfile}
                </span>
              )}
              <MarketPositionBadge plan={plan} />
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              {submitted}/{plan.totalSlices} submitted · {remaining} pending{failed > 0 ? ` · ${failed} failed` : ''}
            </p>
            {nextSlice && plan.status === 'active' && (
              <p className="text-gray-400 text-xs mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                Next fires {fmtFutureTime(nextSlice.scheduledAt)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={() => onDuplicate?.(plan)} disabled={busy}
              title="Duplicate this plan into a new one"
              className="p-1.5 rounded-lg bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600 disabled:opacity-50">
              <Copy className="w-3.5 h-3.5" />
            </button>
            {plan.status === 'active' && (
              <button onClick={() => onCancel(plan.planId)} disabled={busy}
                title="Stop future slice submissions"
                className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
        <SliceTimeline plan={plan} />
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[dca-fade_0.2s_ease-out]">
          <style>{`@keyframes dca-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {/* Progress KPIs */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Invested</div>
              <div className="text-xs text-white font-mono">{formatBaseUnits(investedSellBase.toString(), sellDec)} {plan.sellSymbol}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{submitted}/{plan.totalSlices} slices</div>
            </div>
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Accumulated (target)</div>
              <div className="text-xs text-emerald-400 font-mono">{formatBaseUnits(filledBuyBase.toString(), buyDec)} {plan.buySymbol}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{filled} filled</div>
            </div>
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Per slice</div>
              <div className="text-xs text-white font-mono">{formatBaseUnits(plan.sellAmountPerSlice, sellDec)} {plan.sellSymbol}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{(() => {
                // Plan record stores intervalSec — convert to a human label.
                const s = Number(plan.intervalSec);
                if (!Number.isFinite(s) || s <= 0) return plan.frequency || '—';
                if (s === 86400)        return 'Daily';
                if (s === 7 * 86400)    return 'Weekly';
                if (s === 14 * 86400)   return 'Biweekly';
                if (s === 30 * 86400)   return 'Monthly';
                // Custom intervals — show as "every Xh" or "every Xd"
                if (s < 86400)          return `every ${Math.round(s / 3600)}h`;
                return `every ${Math.round(s / 86400)}d`;
              })()}</div>
            </div>
          </div>

          {/* Per-slice table */}
          <div className="bg-dark-700/30 rounded-xl border border-dark-600 overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-dark-800/60">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Scheduled</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Fired at</div>
              <div className="col-span-3 text-right">Order</div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {plan.schedule?.map((s, idx) => {
                const stateLabel = s.status === 'filled'    ? 'filled'
                                : s.status === 'submitted' ? 'live on orderbook'
                                : s.status === 'failed'    ? 'failed'
                                : s.status === 'cancelled' ? 'cancelled'
                                : 'pending';
                const firedAt = s.filledAt || s.submittedAt;
                return (
                  <div key={s.sliceIndex} className={`grid grid-cols-12 px-3 py-1.5 text-xs ${idx < plan.schedule.length - 1 ? 'border-b border-dark-600/30' : ''} hover:bg-dark-600/20`}>
                    <div className="col-span-1 text-gray-500 font-mono">#{s.sliceIndex + 1}</div>
                    <div className="col-span-3 text-gray-300 font-mono">{shortDateMonth(s.scheduledAt)}</div>
                    <div className={`col-span-2 ${sliceStateCls[s.status] || 'text-gray-400'}`}>{stateLabel}</div>
                    <div className="col-span-3 text-gray-400 font-mono">{firedAt ? fmtSliceTime(Math.floor(Date.parse(firedAt) / 1000)) : '—'}</div>
                    <div className="col-span-3 text-right text-gray-500 font-mono truncate" title={s.orderId || '—'}>
                      {s.orderId ? `${String(s.orderId).slice(0, 6)}…` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Plan ID + metadata */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2 space-y-1.5">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Plan ID</div>
              <button onClick={() => copyToClipboard(plan.planId)} title="Copy"
                className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
                {plan.planId}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-dark-600/40">
              <div><span className="text-gray-500">Created:</span> <span className="text-gray-300">{fmtTime(plan.createdAt)}</span></div>
              {plan.cancelledAt && (
                <div><span className="text-red-400">Cancelled:</span> <span className="text-gray-300">{fmtTime(plan.cancelledAt)}</span></div>
              )}
              {plan.completedAt && (
                <div><span className="text-blue-300">Completed:</span> <span className="text-gray-300">{fmtTime(plan.completedAt)}</span></div>
              )}
              <div><span className="text-gray-500">Chain:</span> <span className="text-gray-300">{plan.chainId}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Slice timeline — visual progress strip for an active plan ───────────────
// Renders the N slices on a horizontal timeline with status-coloured dots and
// the scheduled date below each. Beats the "1/4 submitted" text-only line by
// making it obvious where you are in the plan + when each slice fires.
function SliceTimeline({ plan }) {
  const slices = plan.schedule || [];
  if (slices.length === 0) return null;
  const submittedCount = slices.filter(s => s.status === 'submitted' || s.status === 'filled').length;
  const failedCount    = slices.filter(s => s.status === 'failed').length;
  const pct = Math.round((submittedCount / slices.length) * 100);

  function dotClass(s) {
    if (s.status === 'filled')    return 'bg-emerald-400 ring-emerald-400/30';
    if (s.status === 'submitted') return 'bg-blue-400 ring-blue-400/30';
    if (s.status === 'failed')    return 'bg-red-400 ring-red-400/30';
    if (s.status === 'cancelled') return 'bg-dark-500 ring-dark-500/30';
    return 'bg-dark-600 ring-dark-600/30'; // pending
  }
  function shortDate(sec) {
    try { return new Date(sec * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch { return '—'; }
  }

  return (
    <div className="mt-3 pt-3 border-t border-dark-700/40">
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="text-gray-400">Slice schedule</span>
        <span className="text-gray-500">{pct}% submitted{failedCount > 0 ? ` · ${failedCount} failed` : ''}</span>
      </div>
      <div className="relative h-1 bg-dark-700 rounded-full mb-2 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${slices.length}, minmax(0, 1fr))` }}>
        {slices.map(s => (
          <div key={s.sliceIndex} className="flex flex-col items-center gap-1">
            <div className={`w-2.5 h-2.5 rounded-full ring-2 ${dotClass(s)}`} />
            <span className="text-[10px] text-gray-500">{shortDate(s.scheduledAt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function DexDcaPage() {
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [busy, setBusy]       = useState(null);
  const [chartPair, setChartPair] = useState('ETH/USDT');
  // formKey + initialValues lets us re-mount CreatePlanPanel with pre-filled
  // values when the user clicks Duplicate on an existing plan.
  const [formKey, setFormKey]   = useState(0);
  const [initialValues, setInitialValues] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDexDcaPlans();
      // Show ONLY recurring DCA plans on this page. One-shot plans created
      // through the Algo Orders surface live there to keep the two features
      // visually distinct.
      const all = r.data?.plans || [];
      setPlans(all.filter(p => p.executionMode !== 'one_shot'));
    }
    catch (err) { setToast({ type: 'error', message: err.message || 'Could not load plans' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (plan) => {
    if (plan) setPlans(prev => [plan, ...prev.filter(p => p.planId !== plan.planId)]);
  };

  const handleDuplicate = (plan) => {
    const freq = FREQUENCY_OPTIONS.find(f => f.sec === Number(plan.intervalSec));
    setInitialValues({
      protocol:     plan.protocol,
      chainId:      Number(plan.chainId),
      sellSym:      plan.sellSymbol,
      buySym:       plan.buySymbol,
      sellPerSlice: plan.sellAmountPerSlice && plan.sellDecimals
        ? formatBaseUnits(plan.sellAmountPerSlice, plan.sellDecimals, 8) : '',
      totalSlices:  Number(plan.totalSlices),
      freqId:       freq?.id || 'weekly',
      pricingModel: plan.pricingModel,
      slippagePct:  plan.slippagePct ?? 1,
      limitPrice:   plan.limitPrice || '',
      name:         plan.name ? `${plan.name} (copy)` : '',
    });
    setFormKey(k => k + 1);
    setToast({ type: 'success', message: 'Plan cloned into the form. Review then click Create.' });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = async (planId) => {
    setBusy(planId);
    try {
      const r = await cancelDexDcaPlan(planId);
      setPlans(prev => prev.map(p => p.planId === planId ? r.data?.plan || p : p));
      setToast({ type: 'success', message: 'Future slices cancelled. Already-submitted orders continue independently.' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Cancel failed' });
    } finally { setBusy(null); }
  };

  const active = plans.filter(p => p.status === 'active');
  const history = plans.filter(p => p.status !== 'active');

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent-primary/15 text-accent-primary flex items-center justify-center">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX DCA</h1>
            <p className="text-gray-400 text-sm">Recurring scheduled buys. Sign each slice once, the platform handles the rest.</p>
          </div>
        </div>
      </div>

      <HowItWorksPanel />

      {/* TradingView chart — follows your sell/buy selection. Reference price
          from CEX feeds since the underlying asset price is identical to DEX. */}
      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView · price reference only</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreatePlanPanel key={formKey} initialValues={initialValues}
          onCreated={handleCreated} onToast={setToast}
          onPairChange={({ sellSym, buySym }) => setChartPair(chartPairFor(sellSym, buySym))} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active plans</h3>
              <p className="text-gray-500 text-xs">{active.length} running</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Calendar className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active DCA plans. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(p => <PlanRow key={p.planId} plan={p} onCancel={handleCancel} onDuplicate={handleDuplicate} busy={busy === p.planId} />)}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(p => <PlanRow key={p.planId} plan={p} onCancel={handleCancel} onDuplicate={handleDuplicate} busy={busy === p.planId} />)}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
